/**
 * Runtime context data supplied to a {@link CompactionStrategy}
 * when deciding whether to compact the context window.
 *
 * @docLink packages/runner/dev-guide#compaction
 */
export interface CompactionContext {
  estimatedTokens: number;
  contextWindow: number;
  messageCount: number;
  lastCompactionSeq: number | null;
  timeSinceLastCompaction: number | null;
}

/**
 * Strategy for deciding when the context window should be compacted.
 *
 * Implementations receive the current {@link CompactionContext} and
 * return true when compaction should proceed.
 *
 * @docLink packages/runner/dev-guide#compaction
 */
export interface CompactionStrategy {
  shouldCompact(context: CompactionContext): boolean;
}

/**
 * Compaction strategy that triggers when estimated token usage
 * exceeds a percentage of the context window. A cooldown period
 * prevents thrashing.
 *
 * @docLink packages/runner/dev-guide#compaction
 */
export class TokenThresholdStrategy implements CompactionStrategy {
  private readonly thresholdPercent: number;
  private readonly minCooldownMs: number;

  constructor(opts: { thresholdPercent: number; minCooldownMs: number }) {
    this.thresholdPercent = opts.thresholdPercent;
    this.minCooldownMs = opts.minCooldownMs;
  }

  shouldCompact(context: CompactionContext): boolean {
    const fillPercent = (context.estimatedTokens / context.contextWindow) * 100;
    if (fillPercent < this.thresholdPercent) return false;

    // Respect cooldown
    if (
      context.timeSinceLastCompaction !== null &&
      context.timeSinceLastCompaction < this.minCooldownMs
    ) {
      return false;
    }

    return true;
  }
}
